import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { QRBlock } from "@/components/QRCode";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { SectionLoader } from "@/components/common";
import { downloadVCard } from "@/lib/vcard";
import { MemberCardFlip } from "@/components/member-card/MemberCardFlip";
import { PresentCardDialog } from "@/components/member-card/PresentCardDialog";
import { CardCustomizer } from "@/components/member-card/CardCustomizer";
import { Contact, RefreshCw, ShieldCheck, QrCode, MousePointerClick, Loader2, Sparkles, Palette, IdCard } from "lucide-react";

const DISPLAY_DEFAULTS = { title: true, company: true, email: false, phone: false, website: false };

export default function MemberCard() {
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [display, setDisplay] = useState(DISPLAY_DEFAULTS);

  const load = async () => {
    try {
      const res = await api.get("/cards/me");
      setCard(res.data);
      setDisplay({ ...DISPLAY_DEFAULTS, ...(res.data.card_display || {}) });
    } catch (e) {
      toast.error("Could not load your member card");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cardToken = card?.card_token;
  const shareUrl = useMemo(
    () => (cardToken ? `${window.location.origin}/m/${cardToken}` : ""),
    [cardToken]
  );

  const regenerate = async () => {
    setResetting(true);
    try {
      const res = await api.post("/cards/me/regenerate");
      setCard((c) => ({ ...c, card_token: res.data.card_token }));
      toast.success("Your card link has been reset — old links no longer work");
    } catch (e) {
      toast.error("Could not reset your card link");
    } finally {
      setResetting(false);
    }
  };

  const updateStyle = async (patch) => {
    setCard((c) => ({ ...c, ...patch }));
    try {
      await api.put("/users/me", patch);
    } catch (e) {
      toast.error("Could not save your card style");
    }
  };

  const updateDisplay = async (key, value) => {
    const next = { ...display, [key]: value };
    setDisplay(next);
    setCard((c) => ({ ...c, card_display: next }));
    try {
      await api.put("/users/me", { card_display: next });
    } catch (e) {
      setDisplay(display); // revert
      toast.error("Could not save your name badge settings");
    }
  };

  const saveContact = () => {
    if (!card) return;
    downloadVCard({
      name: card.name,
      title: card.title,
      company: card.company,
      email: card.email,
      phone: card.phone,
      website: card.website,
      linkedin: card.linkedin,
      location_text: card.location_text,
      bio: card.bio,
    });
    toast.success("Contact card (.vcf) downloaded");
  };

  const isSparse = card && !card.headline && !card.bio && !(card.interests || []).length && !card.seeking;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">My Member Card</h1>
          <p className="mt-1 text-muted-foreground">
            Tap the card to flip it. Show your QR at events — anyone can scan to view your profile and save you as a contact.
          </p>
        </div>
        <Button
          onClick={() => setPresenting(true)}
          disabled={!shareUrl}
          className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]"
          data-testid="present-card-trigger"
        >
          <QrCode className="mr-2 h-4 w-4" /> Present my card
        </Button>
      </div>

      {loading ? (
        <SectionLoader label="Preparing your card…" />
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[1.1fr_1fr]">
          {/* Premium flip card */}
          <div className="space-y-3">
            <MemberCardFlip
              member={card}
              shareUrl={shareUrl}
              flipped={flipped}
              onToggleFlip={setFlipped}
              onPresent={() => setPresenting(true)}
              onEdit={() => navigate("/portal/account/profile")}
            />
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <MousePointerClick className="h-3.5 w-3.5" /> Tap the card to see the {flipped ? "front" : "back"}
              </p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="card-customize-trigger">
                    <Palette className="mr-1.5 h-4 w-4" /> Customize
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72" align="center" data-testid="card-customize-popover">
                  <CardCustomizer
                    accent={card.card_accent}
                    cover={card.card_cover}
                    onAccentChange={(k) => updateStyle({ card_accent: k })}
                    onCoverChange={(url) => updateStyle({ card_cover: url || "" })}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Actions + info */}
          <div className="space-y-4">
            {isSparse && (
              <Card className="border-primary/30 bg-primary/5" data-testid="member-card-complete-prompt">
                <CardContent className="flex items-start gap-3 p-5">
                  <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div className="text-sm">
                    <p className="font-semibold text-foreground">Make your card shine</p>
                    <p className="mt-0.5 text-muted-foreground">
                      Add a photo, a short “ask me about”, your interests, and what you’re looking for.{" "}
                      <Link to="/portal/account/profile" className="font-semibold text-primary hover:underline" data-testid="member-card-complete-link">
                        Complete your profile
                      </Link>.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-border bg-card">
              <CardContent className="space-y-4 p-6">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary"><QrCode className="h-5 w-5 text-primary" /></div>
                  <div>
                    <h3 className="font-semibold text-foreground">Share your QR</h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">Download, print, copy or share your card link.</p>
                  </div>
                </div>
                <QRBlock
                  value={shareUrl}
                  size={150}
                  fileName={`member-card-${(card?.name || "me").replace(/\s+/g, "-").toLowerCase()}`}
                  printTitle={card?.name || "Friday Conversations"}
                  printSubtitle="Friday Conversations · Scan to connect"
                  testId="member-card-share-qr"
                />
              </CardContent>
            </Card>

            <Card className="border-border bg-card" data-testid="name-tag-settings">
              <CardContent className="space-y-4 p-6">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary"><IdCard className="h-5 w-5 text-primary" /></div>
                  <div>
                    <h3 className="font-semibold text-foreground">Name badge &amp; on-screen plate</h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">Choose what shows on your printed event badge and when your name appears on the live podcast stream. Your name always shows.</p>
                  </div>
                </div>

                {/* Live preview of the lower-third / badge text */}
                <div className="overflow-hidden rounded-lg" style={{ background: "#0a1532" }} data-testid="name-tag-preview">
                  <div className="flex items-stretch">
                    <div className="w-1.5 shrink-0" style={{ background: "#c9772e" }} />
                    <div className="min-w-0 px-4 py-3">
                      <p className="truncate font-serif text-base font-semibold text-white">{card.name || "Your name"}</p>
                      {((display.title && card.title) || (display.company && card.company)) && (
                        <p className="truncate text-sm" style={{ color: "#e2c4a3" }}>
                          {[display.title && card.title, display.company && card.company].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      {[display.phone && card.phone, display.email && card.email, display.website && card.website].filter(Boolean).length > 0 && (
                        <p className="truncate text-xs text-white/70">
                          {[display.phone && card.phone, display.email && card.email, display.website && card.website].filter(Boolean).join("  •  ")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Field toggles */}
                <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">
                  {[
                    { key: "title", label: "Job title", value: card.title },
                    { key: "company", label: "Company / business", value: card.company },
                    { key: "phone", label: "Phone", value: card.phone },
                    { key: "email", label: "Email", value: card.email },
                    { key: "website", label: "Website", value: card.website },
                  ].map((f) => (
                    <div key={f.key} className="flex items-center justify-between gap-3 px-4 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{f.label}</p>
                        <p className="truncate text-xs text-muted-foreground">{f.value ? f.value : <span className="italic">Add it in your profile first</span>}</p>
                      </div>
                      <Switch checked={!!display[f.key]} onCheckedChange={(v) => updateDisplay(f.key, v)} disabled={!f.value} data-testid={`name-tag-toggle-${f.key}`} />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Used for your printed event name badge and the on-screen name plate during the live show.</p>
              </CardContent>
            </Card>

            <Card className="border-border bg-card">
              <CardContent className="space-y-4 p-6">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary"><Contact className="h-5 w-5 text-primary" /></div>
                  <div>
                    <h3 className="font-semibold text-foreground">Save your own contact card</h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">Download a .vcf to keep on your phone or send directly.</p>
                  </div>
                </div>
                <Button onClick={saveContact} variant="secondary" className="w-full" data-testid="member-card-vcard">
                  <Contact className="mr-2 h-4 w-4" /> Download my vCard
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border bg-secondary/40">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 text-primary" />
                  <div className="text-sm text-muted-foreground">
                    Your public card only shows the fields you’ve made visible.{" "}
                    <Link to="/portal/account/profile" className="font-semibold text-primary hover:underline" data-testid="member-card-privacy-link">
                      Manage what’s shown
                    </Link>{" "}
                    in Profile &amp; Privacy.
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card">
              <CardContent className="flex items-center justify-between gap-4 p-5">
                <div className="flex items-start gap-3">
                  <RefreshCw className="mt-0.5 h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Reset card link</p>
                    <p className="text-xs text-muted-foreground">Generates a new QR and disables old shared links.</p>
                  </div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="member-card-reset">Reset</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="member-card-reset-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Reset your card link?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Any QR codes you’ve already shared or printed will stop working. You’ll get a fresh QR to share.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={regenerate} disabled={resetting} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="member-card-reset-confirm">
                        {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, reset link"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <PresentCardDialog open={presenting} onOpenChange={setPresenting} member={card} shareUrl={shareUrl} />
    </div>
  );
}
